import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { BrowserRouter, Routes, Route, useLocation, useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { initGA, usePageviewTracking } from "./utils/analytics";
import HomepageSwitcher from "./pages/gd/HomepageSwitcher";
import ExclusiveDealsListingV2 from "./pages/gd/v2/ExclusiveDealsListing";
import DestinationsListingV2 from "./pages/gd/v2/DestinationsListing";
import LimitedPromoListingV2 from "./pages/gd/v2/LimitedPromoListing";
import GdBrandsPageV2 from "./pages/gd/v2/GdBrandsPage";
import GroundTourListingV2 from "./pages/gd/v2/GroundTourListing";
import CustomizeTourFormPage from "./pages/gd/customize-tour/CustomizeTourFormPage";
import ListingPageSwitcher from "./pages/gd/ListingPageSwitcher";
import Header from "./pages/gd/Header";
import Footer from "./pages/gd/Footer";
import HeaderV2 from "./pages/gd/v2/Header";
import FooterV2 from "./pages/gd/v2/Footer";
import ShowPage from "./pages/gd/ShowPage";
import GroundTourShowPage from "./pages/gd/ground-tour/GroundTourShowPage";
import PostsShowPage from "./pages/gd/PostsShowPage";
import MiceAboutPage from "./pages/gd/MiceAboutPage";
import CorporateMicePage from "./pages/gd/CorporateMicePage";
import CorporateBusinessTravelPage from "./pages/gd/CorporateBusinessTravelPage";
import CorporateGdWarrantPage from "./pages/gd/CorporateGdWarrantPage";
import NewsAndBlogsPage from "./pages/gd/NewsAndBlogsPage";
import NewsAndBlogsShowPage from "./pages/gd/NewsAndBlogsShowPage";
import GalleryPage from "./pages/gd/GalleryPage";
import GalleryShowPage from "./pages/gd/GalleryShowPage";
import PartnerSignInPage from "./pages/gd/PartnerSignInPage";
import AboutUsPage from "./pages/gd/AboutUsPage";
import ContactUsPage from "./pages/gd/ContactUsPage";
import ContactAgentsPage from "./pages/gd/ContactAgentsPage";
import GDFlightContactAgentsPage from "./pages/gd/GDFlightContactAgentsPage";
import GDFlightAboutPage from "./pages/gd/GDFlightAboutPage";
import WhatsNewPage from "./pages/gd/WhatsNewPage";
import WhatsNewShowPage from "./pages/gd/WhatsNewShowPage";
import SeriesPage from "./pages/gd/SeriesPage";
import SeriesShowPage from "./pages/gd/SeriesShowPage";
import FooterStaticPage from "./pages/gd/FooterStaticPage";
import PostShowPage from "./pages/gd/PostShowPage";
import IceVisaContactAgentsPage from "./pages/gd/IceVisaContactAgentsPage";
import IceVisaAboutPage from "./pages/gd/IceVisaAboutPage";
import ResetPasswordPage from "./pages/gd/ResetPasswordPage";
import ScrollToTop from "./layouts/ScrollToTop";
import FloatingChatButton from "./layouts/FloatingChatButton";
// Import your new Voice Over Ad player component here
import VoiceOverAdButton from "./layouts/VoiceOverAdButton";
import PostsPage from "./pages/gd/PostsPage";
import BookingHistoryPage from "./pages/gd/BookingHistoryPage";
import PaymentStatus from "./pages/gd/PaymentStatus";
import SignInPage from "./pages/gd/SignInPage";
import ProfilePage from "./pages/gd/ProfilePage";
import InvalidReferralLink from "./components/shared/InvalidReferralLink";
import OrderInformationPage from "./pages/gd/OrderInformationPage";
import GroundTourOrderInformationPage from "./pages/gd/GroundTourOrderInformationPage";
import DedicatedPaymentPage from "./pages/gd/DedicatedPaymentPage";
import ExpiredLinkPage from "./pages/gd/ExpiredLinkPage";
import B2C2BApi from "./api-services/b2c2b-api.service";
import Cookies from "js-cookie"
import MainLayout from "./layouts/gd/MainLayout";
import MagazinePage from "./pages/gd/MagazinePage"
import MagazineReaderPage from "./pages/gd/MagazineReaderPage";
import { AuthProvider } from "./services/AuthContext";
import { InsuranceProvider } from "./services/InsuranceContext";
import { SettingsProvider, useIsNewLayout, useSettings } from "./services/SettingsContext";
import { isB2C2B, SIGNIN_URL } from "./utils/helpers";

const customCookies = Cookies.withAttributes({
  expires: 14
});

function LayoutWrapper(props) {
  const location = useLocation();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [hasVoiceOverAd, setHasVoiceOverAd] = useState(false);
  const isItineraryShowPage = location.pathname.includes("itinerary") || location.pathname.includes("ground-tour");
  const isHomepage = location.pathname === "/web" || location.pathname === "/web/";
  // The redesigned header and footer now ship on every /web page, gated only on
  // the new-layout flag rather than on which route is being rendered.
  const isNewLayout = useIsNewLayout();

  // Exact sizes
  const BTN = 54;
  const GAP = 12; // Tight gap setting
  const chatBottomBase = isItineraryShowPage ? 100 : 24;

  // Lock the position to sit directly above the chat button at all times
  const voiceOverBottom = chatBottomBase + BTN + GAP; // 24 + 54 + 12 = 90px

  return (
    <>
      <MainLayout
        Header={
          isNewLayout ? (
            <HeaderV2 serverSideName={props.username} isPartner={props.isPartner} />
          ) : (
            <Header serverSideName={props.username} isPartner={props.isPartner} />
          )
        }
        Footer={isNewLayout ? <FooterV2 /> : <Footer />}
      />

      {/* Floating Elements Stack */}
      {isHomepage && !isChatOpen && (
        <VoiceOverAdButton
          bottomOffset={voiceOverBottom}
          onVisibilityChange={setHasVoiceOverAd}
        />
      )}

      <FloatingChatButton
        isItineraryShowPage={isItineraryShowPage}
        showBackTop={true}
        bottomBase={chatBottomBase}
        onToggle={setIsChatOpen}
        hasVoiceOverAd={isHomepage && !isChatOpen && hasVoiceOverAd}
      />
    </>
  );
}

// Redesigned header/footer used by the /web homepage when the version setting
// selects the new layout.
function V2LayoutWrapper(props) {
  return (
    <MainLayout
      Header={<HeaderV2 serverSideName={props.username} isPartner={props.isPartner} />}
      Footer={<FooterV2 />}
    />
  );
}

export default function gd(props: {isUserSignedIn:boolean, username: string, isPartner: boolean, gd_policy: string, gd_benefits: string, gd_endorsement: string, google_tracking_id?: string}) {
  initGA(props.google_tracking_id);

  const ProtectedRoute = () => {
    if (!props.isUserSignedIn) {
      return <Navigate to={SIGNIN_URL} replace />;
    }
  
    return <LayoutWrapper {...props} />;
  };

  const Inner = () =>{
    const location = useLocation();
    
    const [searchParams] =  useSearchParams();
    const navigate =  useNavigate();

    const { loading: settingsLoading } = useSettings();

    usePageviewTracking();

    useEffect(() => {
      const legacyGroundTourPayment = location.hash.match(/^#\/payments\/ground_tour\/(\d+)$/);
      if (legacyGroundTourPayment) {
        navigate(`/web/payments/${legacyGroundTourPayment[1]}`, { replace: true });
        return;
      }

      let active = true;
      const agencyRef = searchParams.get('ref');
      const keyword = searchParams.get('keyword');
    
      if (location.hash) {
        const element = document.getElementById(location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          window.history.replaceState("", document.title, location.pathname);
        }
      }
    
      if (agencyRef) {
        B2C2BApi.validateAgentReferralLink(agencyRef, keyword)
          .then(referral => {
            if (active && referral) {
              const agencyAttributes = referral?.agency?.attributes || {};
              const referralEntityType = agencyAttributes.referral_entity_type;
              const masterAgent = agencyAttributes.master_agent;
              const agency = {
                id: referral?.agency?.id,
                name: agencyAttributes.name,
                full_name: agencyAttributes.full_name,
                emails: agencyAttributes.emails,
                phones: agencyAttributes.phones,
                address: agencyAttributes.address,
                city: agencyAttributes.city,
                state: agencyAttributes.state,
                masterAgent,
                subagent: referralEntityType === "subagent" ? masterAgent : null,
                referralEntityType,
                referralEntityId: agencyAttributes.referral_entity_id,
                referralEntityName: agencyAttributes.referral_entity_name,
                link: window.location.href
              };
              customCookies.set("agency", JSON.stringify(agency));
              B2C2BApi.trackClick(agencyRef, keyword).catch(() => {});

              if (!location.pathname.includes("itinerary") && !location.pathname.includes("ground-tour")) {
                if (!keyword) {
                  navigate("/web");
                  return;
                }
                navigate(`/web/listing?keyword=${keyword}`);
              }
            }
          }).catch(error => {
            if (active) {
              navigate('/web/invalid-link');
            }
          });
      }
    
      return () => {
        active = false;
      };
    }, [location.hash, location.pathname, searchParams, navigate]);

    const isVersionGatedRoute =
      location.pathname === "/web" || location.pathname === "/web/" || location.pathname === "/web/listing";
    if (isVersionGatedRoute && settingsLoading) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spin size="large" />
        </div>
      );
    }

    return (
      <Routes>
        <Route path="/web/exclusive-deals" element={<V2LayoutWrapper {...props} />}>
          <Route index element={<ExclusiveDealsListingV2 />} />
        </Route>

        <Route path="/web/destinations" element={<V2LayoutWrapper {...props} />}>
          <Route index element={<DestinationsListingV2 />} />
        </Route>

        <Route path="/web/limited-promo" element={<V2LayoutWrapper {...props} />}>
          <Route index element={<LimitedPromoListingV2 />} />
        </Route>

        <Route path="/web/gd-brands" element={<V2LayoutWrapper {...props} />}>
          <Route index element={<GdBrandsPageV2 />} />
        </Route>

        <Route path="/web/ground-tour" element={<V2LayoutWrapper {...props} />}>
          <Route index element={<GroundTourListingV2 />} />
          <Route path=":code" element={<GroundTourShowPage />} />
        </Route>

        <Route path="/web/customize-tour" element={<V2LayoutWrapper {...props} />}>
          <Route index element={<CustomizeTourFormPage />} />
        </Route>

        <Route path="/web/customize-tour" element={<V2LayoutWrapper {...props} />}>
          <Route index element={<CustomizeTourFormPage />} />
        </Route>

        <Route path="/web" element={<LayoutWrapper {...props} />}>
          <Route index element={<HomepageSwitcher />} />
          <Route path="/web/listing" element={<ListingPageSwitcher />} />
          <Route path="/web/itinerary/:id" element={<ShowPage isPartner={props.isPartner} />} />
          <Route path="/web/series-tour/:type" element={<SeriesPage />} />
          <Route path="/web/series-tour/:type/:id" element={<SeriesShowPage />} /> 
          <Route path="/web/posts/:type" element={<PostsPage />} />
          <Route path="/web/posts/:type/:id" element={<PostsShowPage />} />
          <Route path="/web/mice/about" element={<MiceAboutPage />} />
          <Route path="/web/corporate/mice" element={<CorporateMicePage />} />
          <Route path="/web/corporate/business-travel" element={<CorporateBusinessTravelPage />} />
          <Route path="/web/corporate/gd-warrant" element={<CorporateGdWarrantPage />} />
          <Route path="/web/post/:type" element={<PostShowPage />} />
          <Route path="/web/magazine" element={<MagazinePage />} />
          <Route path="/web/magazine/:slug" element={<MagazineReaderPage />}/>
          <Route path="/web/ice-visa-about-us" element={<IceVisaAboutPage />} />
          <Route path="/web/ice-visa-services-agents" element={<IceVisaContactAgentsPage />} />
          <Route path="/web/blogs" element={<NewsAndBlogsPage />} />
          <Route path="/web/blogs/:id" element={<NewsAndBlogsShowPage />} />
          <Route path="/web/gallery" element={<GalleryPage />} />
          <Route path="/web/gallery/:id" element={<GalleryShowPage />} />
          <Route path="/web/new-posts" element={<WhatsNewPage />} />
          <Route path="/web/new-posts/:id" element={<WhatsNewShowPage />} />
          
          <Route path="/web/partner-sign-in" element={<PartnerSignInPage />} />
          <Route path="/web/about-us" element={<AboutUsPage />} />
          <Route path="/web/contact-us" element={<ContactUsPage />} />
          <Route path="/web/contact-agents" element={<ContactAgentsPage />} />
          <Route path="/web/gd-flight-about-us" element={<GDFlightAboutPage />} />
          <Route path="/web/gd-flight-agents" element={<GDFlightContactAgentsPage />} />
          <Route path="/web/footer/:type" element={<FooterStaticPage />} />
          <Route path="/web/:scope/reset-password" element={<ResetPasswordPage />} />

          {
            isB2C2B && (
              <>
                <Route path="/web/sign-in" element={<SignInPage />} />
                <Route path="/web/payments/:id" element={<PaymentStatus />} />
                <Route path="/web/bookings/:id/payment" element={<DedicatedPaymentPage />} />
                <Route path="/web/invalid-link" element={<InvalidReferralLink />} />
                <Route path="/web/expired-link" element={<ExpiredLinkPage />} />
              </>
            )
          }
        </Route>
      </Routes>
    )
  
  }

  return (
      <BrowserRouter>
       <AuthProvider>
        <SettingsProvider>
            <ScrollToTop/>
            <Inner/>

            {/* separate layout for protected routes */}
            <InsuranceProvider value={{ gd_policy: props.gd_policy, gd_benefits: props.gd_benefits, gd_endorsement: props.gd_endorsement }}>
              <Routes>
                <Route element={<ProtectedRoute />}>
                  <Route path="/web/profile" element={<ProfilePage />}/>   
                  <Route path="/web/bookings" element={<BookingHistoryPage />} />
                  <Route path="/web/bookings/ground/:id" element={<GroundTourOrderInformationPage />} />
                  <Route path="/web/bookings/:id" element={<OrderInformationPage />} />         
                </Route>
              </Routes>
            </InsuranceProvider>
        </SettingsProvider>
       </AuthProvider>
      </BrowserRouter>
  );
}
